import logIfNotInTestSuite from '../logIfNotInTestSuite';

// Temporary leak-detection instrumentation for the SOA/Shift Digital investigation.
// Retire by deleting:
// - this file
// - the callOnReceive method and its call site in dataMapper.js
// - the onReceive export, the onSend call site, and the sending() parameters
//   added for this, in shiftDr/utils.js
// - the ws-tagging-shiftdr-cdl-syndication default in config/feature-flags.json
// - the leak-detection tests in shiftdr.test.js, shiftLeakDetection.test.js and
//   dataMapper.test.js (callOnReceive)

const FEATURE_FLAG_KEY = 'ws-tagging-shiftdr-cdl-syndication';

// AMD External Event -> event.tagIndex, matching vdp.js's own rule.logic strings exactly,
// except DR_TRADEIN_VEHICLE_OWNERSHIP_CHANGED - Nathaniel confirmed in Pixall data that AMD
// only ever sends the "D" variant in production, unlike the spec/vdp.js's shorter string.
// commonDataLayerEvent (CDL/UDS) and ws-track-my-offer rules never set eventData.eventName,
// so they naturally miss this lookup and stay out of scope.
// tagIndex prefix is amd-ext (not dri-ext) - dri is already used elsewhere for Ecommerce
// reporting, and reusing it here would inflate those numbers.
export const AMD_EVENT_TAG_INDEX = {
	'Shopper Platform Init': 'amd-ext-1',
	DR_PAYMENT_TYPE_CHANGED: 'amd-ext-2',
	DR_DOWN_PAYMENT_CHANGED: 'amd-ext-3',
	DR_CREDIT_TIER_CHANGED: 'amd-ext-4',
	DR_PAYMENT_TERM_CHANGED: 'amd-ext-5',
	DR_LEASE_ANNUAL_MILES_CHANGED: 'amd-ext-6',
	DR_TRADEIN_CHANGED: 'amd-ext-7',
	DR_CONTINUE_TO_SDP_CLICKED: 'amd-ext-8',
	DR_LEAD_FORM_FIRSTNAME_CHANGED: 'amd-ext-9',
	DR_LEAD_FORM_LASTNAME_CHANGED: 'amd-ext-10',
	DR_LEAD_FORM_EMAIL_CHANGED: 'amd-ext-11',
	DR_LEAD_FORM_PHONE_CHANGED: 'amd-ext-12',
	DR_OFFER_SUBMISSION_CLICKED: 'amd-ext-13',
	DR_TEST_DRIVE_REQUESTED: 'amd-ext-14',
	DR_TRADEIN_ESTIMATE_DISPLAYED: 'amd-ext-15',
	DR_CREDIT_APP_SUCCESS_DISPLAYED: 'amd-ext-16',
	DR_SHOPPER_COMPLETES_MENU_PRODUCTS: 'amd-ext-17',
	DR_ACCESSORIES_CLOSE: 'amd-ext-18',
	DR_INCENTIVES_ADDED: 'amd-ext-19',
	DR_PAYMENT_DISPLAY: 'amd-ext-20',
	DR_SDP_LEAVE_BUTTON_CLICKED: 'amd-ext-21',
	DR_CREDIT_APP_DISPLAYED: 'amd-ext-22',
	DR_CREDIT_APP_PERSONALINFO_PAGE_DISPLAYED: 'amd-ext-23',
	DR_TRADEIN_VEHICLE_YEAR_CHANGED: 'amd-ext-24',
	DR_TRADEIN_VEHICLE_TRIM_CHANGED: 'amd-ext-25',
	DR_TRADEIN_SEARCHTYPE_CLICKED: 'amd-ext-26',
	DR_TRADEIN_VEHICLE_MILEAGE_CHANGED: 'amd-ext-27',
	DR_TRADEIN_VEHICLE_ZIPCODE_CHANGED: 'amd-ext-28',
	DR_TRADEIN_VEHICLE_OWNERSHIP_CHANGED: 'amd-ext-29',
	DR_TRADEIN_OPENED: 'amd-ext-30',
	DR_NAVIGATE_TO_VEHICLEPROTECTION: 'amd-ext-31',
	DR_LEAD_FORM_DISPLAYED: 'amd-ext-32',
	DR_NAVIGATE_TO_ACCESSORIES: 'amd-ext-33',
	DR_MENU_PRODUCT_ADDED: 'amd-ext-34',
	DR_ACCESSORIES_ADDED: 'amd-ext-35',
	DR_NAVIGATE_TO_TESTDRIVE: 'amd-ext-36',
	DR_APPOINTMENT_TYPE_CLICKED: 'amd-ext-37',
	DR_TEST_DRIVE_LOCATION_CLICKED: 'amd-ext-38',
	DR_TEST_DRIVE_TIME_SELECTED: 'amd-ext-39',
	DR_TEST_DRIVE_DATE_SELECTED: 'amd-ext-40'
};

// Never let a CDL failure affect the Shift send or the caller's own reporting.
// A missing coxAutoDataLayer is logged distinctly from a push failure, so a
// missing-CDL session (e.g. consent not yet given) isn't mistaken for a
// genuine AMD leak when Stage 1/Stage 2 counts are compared.
const pushCdlEvent = (win, tagIndex, eventName) => {
	if (!win.coxAutoDataLayer) {
		try {
			logIfNotInTestSuite({
				msg: 'shiftDr CDL leak-detection push skipped - coxAutoDataLayer is missing',
				data: { tagIndex, eventName },
				level: 'warn'
			});
		} catch (error) {
			// never let a logging failure affect the caller either
		}
		return;
	}

	try {
		win.coxAutoDataLayer.push?.({
			event: {
				action: 'Syndicate',
				element: 'External Analytics Event',
				tagIndex,
				name: eventName
			}
		});
	} catch (error) {
		logIfNotInTestSuite({
			msg: 'shiftDr CDL leak-detection push failed',
			data: { tagIndex, eventName, error: error?.message },
			level: 'warn'
		});
	}
};

const pushIfEnabled = ({ win, flags, sourceEventName, pushedEventName }) => {
	if (!flags?.[FEATURE_FLAG_KEY]) return;

	const tagIndex = AMD_EVENT_TAG_INDEX[sourceEventName];
	if (!tagIndex) return;

	pushCdlEvent(win, tagIndex, pushedEventName);
};

// Stage 1: on receipt of the raw AMD event, before any rule matching.
export const onReceive = ({ win, eventData, flags }) => {
	const sourceEventName = eventData?.eventName;
	pushIfEnabled({
		win,
		flags,
		sourceEventName,
		pushedEventName: sourceEventName
	});
};

// Stage 2: right after each sd() call actually made.
export const onSend = ({ win, eventData, sdEventName, flags }) => {
	pushIfEnabled({
		win,
		flags,
		sourceEventName: eventData?.eventName,
		pushedEventName: sdEventName
	});
};
